import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Thermometer, Plane, Camera, UtensilsCrossed, Mountain, Waves, Building, TreePalm, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, StaggerContainer, StaggerItem, CinematicCard } from '../components/CinematicEffects';

const destinationsData = {
  'puerto-escondido': {
    code: 'PXM',
    name: 'Puerto Escondido',
    tagline: { es: 'Paraíso del Surf y Playas Vírgenes', en: 'Surf Paradise and Virgin Beaches' },
    hero: 'https://images.unsplash.com/photo-1582053727234-c29583712a75?w=1920&q=80',
    description: {
      es: 'Puerto Escondido es uno de los destinos de playa más hermosos de México, famoso mundialmente por sus olas perfectas para el surf, especialmente en Playa Zicatela. Este paraíso costero ofrece una combinación única de playas vírgenes, aguas cristalinas, vida nocturna vibrante y una atmósfera relajada que enamora a todos sus visitantes.',
      en: 'Puerto Escondido is one of Mexico\'s most beautiful beach destinations, world-renowned for its perfect surf waves, especially at Zicatela Beach. This coastal paradise offers a unique combination of virgin beaches, crystal-clear waters, vibrant nightlife, and a relaxed atmosphere that captivates all its visitors.'
    },
    highlights: [
      {
        icon: Waves,
        title: { es: 'Playa Zicatela', en: 'Zicatela Beach' },
        description: { es: 'Olas de clase mundial, hogar del Mexican Pipeline. Ideal para surf y para admirar atardeceres espectaculares.', en: 'World-class waves, home of the Mexican Pipeline. Ideal for surfing and spectacular sunsets.' },
        image: 'https://images.unsplash.com/photo-1759661324054-cfd24f2d47c2?w=800&q=80'
      },
      {
        icon: TreePalm,
        title: { es: 'Playa Carrizalillo', en: 'Carrizalillo Beach' },
        description: { es: 'Bahía tranquila perfecta para nadar y snorkel. Rodeada de vegetación tropical y acantilados.', en: 'Tranquil bay perfect for swimming and snorkeling. Surrounded by tropical vegetation and cliffs.' },
        image: 'https://images.unsplash.com/photo-1771636001068-e90df5c47b93?w=800&q=80'
      },
      {
        icon: Star,
        title: { es: 'Laguna de Manialtepec', en: 'Manialtepec Lagoon' },
        description: { es: 'Bioluminiscencia mágica por las noches. Paseos en kayak entre manglares y avistamiento de aves.', en: 'Magical bioluminescence at night. Kayak tours through mangroves and bird watching.' },
        image: 'https://images.unsplash.com/photo-1646970366750-b9f386887e5d?w=800&q=80'
      },
      {
        icon: UtensilsCrossed,
        title: { es: 'Mercado Benito Juárez', en: 'Benito Juárez Market' },
        description: { es: 'Gastronomía oaxaqueña auténtica. Prueba tlayudas, mole y mezcal artesanal en un ambiente local.', en: 'Authentic Oaxacan cuisine. Try tlayudas, mole and artisanal mezcal in a local atmosphere.' },
        image: 'https://images.unsplash.com/photo-1749147074412-9b9d8249ad30?w=800&q=80'
      },
      {
        icon: Camera,
        title: { es: 'La Punta', en: 'La Punta' },
        description: { es: 'Zona hippie-chic con cafés, restaurantes y las mejores vistas del atardecer en Puerto.', en: 'Hippie-chic zone with cafes, restaurants and the best sunset views in Puerto.' },
        image: 'https://images.unsplash.com/photo-1664030391940-261073d09013?w=800&q=80'
      },
      {
        icon: Mountain,
        title: { es: 'Cascadas y Aventura', en: 'Waterfalls and Adventure' },
        description: { es: 'Explora cascadas cercanas, realiza zipline, y disfruta de tours de avistamiento de tortugas.', en: 'Explore nearby waterfalls, zipline, and enjoy turtle watching tours.' },
        image: 'https://images.unsplash.com/photo-1674275791306-c794fc76c459?w=800&q=80'
      }
    ],
    gallery: [
      'https://images.unsplash.com/photo-1582053727234-c29583712a75?w=600&q=80',
      'https://images.unsplash.com/photo-1759661324054-cfd24f2d47c2?w=600&q=80',
      'https://images.unsplash.com/photo-1771636001068-e90df5c47b93?w=600&q=80',
      'https://images.unsplash.com/photo-1664030391940-261073d09013?w=600&q=80',
      'https://images.unsplash.com/photo-1646970366750-b9f386887e5d?w=600&q=80',
      'https://images.unsplash.com/photo-1749147074412-9b9d8249ad30?w=600&q=80'
    ],
    prakticalInfo: {
      climate: { es: 'Cálido tropical todo el año (25-32°C)', en: 'Warm tropical year-round (25-32°C)' },
      bestTime: { es: 'Noviembre a abril (temporada seca)', en: 'November to April (dry season)' },
      airport: { es: '15 min del centro en taxi o Uber', en: '15 min from downtown by taxi or Uber' },
      activities: { es: 'Surf, snorkel, kayak, yoga, vida nocturna', en: 'Surfing, snorkeling, kayaking, yoga, nightlife' }
    }
  },
  'huatulco': {
    code: 'HUX',
    name: 'Huatulco',
    tagline: { es: '9 Bahías Paradisíacas y Naturaleza Virgen', en: '9 Paradise Bays and Virgin Nature' },
    hero: 'https://images.unsplash.com/photo-1749147074412-9b9d8249ad30?w=1920&q=80',
    description: {
      es: 'Huatulco es un tesoro ecológico en la costa de Oaxaca, reconocido por sus 9 bahías vírgenes, 36 playas y el Parque Nacional Huatulco. Este destino combina belleza natural excepcional con desarrollo sustentable, ofreciendo desde playas tranquilas hasta aventuras en la selva tropical.',
      en: 'Huatulco is an ecological treasure on the Oaxacan coast, renowned for its 9 virgin bays, 36 beaches, and Huatulco National Park. This destination combines exceptional natural beauty with sustainable development, offering everything from tranquil beaches to tropical jungle adventures.'
    },
    highlights: [
      {
        icon: Waves,
        title: { es: 'Las 9 Bahías', en: 'The 9 Bays' },
        description: { es: 'Explora Tangolunda, Santa Cruz, Chahué y más. Cada bahía con su encanto único y aguas turquesa.', en: 'Explore Tangolunda, Santa Cruz, Chahué and more. Each bay with its unique charm and turquoise waters.' },
        image: 'https://images.unsplash.com/photo-1771636001068-e90df5c47b93?w=800&q=80'
      },
      {
        icon: TreePalm,
        title: { es: 'Bahía La Entrega', en: 'La Entrega Bay' },
        description: { es: 'Perfecta para snorkel con arrecifes de coral. Aguas tranquilas ideales para familias.', en: 'Perfect for snorkeling with coral reefs. Calm waters ideal for families.' },
        image: 'https://images.unsplash.com/photo-1767733871937-092153f2fbd0?w=800&q=80'
      },
      {
        icon: Mountain,
        title: { es: 'Parque Nacional Huatulco', en: 'Huatulco National Park' },
        description: { es: 'Reserva con selva tropical, cascadas y senderos ecológicos. Rica biodiversidad para explorar.', en: 'Reserve with tropical jungle, waterfalls and eco-trails. Rich biodiversity to explore.' },
        image: 'https://images.unsplash.com/photo-1646970366750-b9f386887e5d?w=800&q=80'
      },
      {
        icon: Camera,
        title: { es: 'Cascadas de Llano Grande', en: 'Llano Grande Waterfalls' },
        description: { es: 'Impresionantes cascadas en medio de la selva. Tour en jeep y natación en pozas naturales.', en: 'Stunning waterfalls in the middle of the jungle. Jeep tour and swimming in natural pools.' },
        image: 'https://images.unsplash.com/photo-1749147074412-9b9d8249ad30?w=800&q=80'
      },
      {
        icon: Building,
        title: { es: 'La Crucecita', en: 'La Crucecita' },
        description: { es: 'Pueblo tradicional con zócalo, iglesia y mercados artesanales. Ambiente auténtico oaxaqueño.', en: 'Traditional town with zocalo, church and craft markets. Authentic Oaxacan atmosphere.' },
        image: 'https://images.unsplash.com/photo-1759661324054-cfd24f2d47c2?w=800&q=80'
      },
      {
        icon: Star,
        title: { es: 'Tours en Barco', en: 'Boat Tours' },
        description: { es: 'Recorre las bahías, visita playas escondidas y avistamiento de delfines y tortugas.', en: 'Tour the bays, visit hidden beaches and spot dolphins and turtles.' },
        image: 'https://images.unsplash.com/photo-1664030391940-261073d09013?w=800&q=80'
      }
    ],
    gallery: [
      'https://images.unsplash.com/photo-1771636001068-e90df5c47b93?w=600&q=80',
      'https://images.unsplash.com/photo-1767733871937-092153f2fbd0?w=600&q=80',
      'https://images.unsplash.com/photo-1646970366750-b9f386887e5d?w=600&q=80',
      'https://images.unsplash.com/photo-1749147074412-9b9d8249ad30?w=600&q=80',
      'https://images.unsplash.com/photo-1759661324054-cfd24f2d47c2?w=600&q=80',
      'https://images.unsplash.com/photo-1664030391940-261073d09013?w=600&q=80'
    ],
    prakticalInfo: {
      climate: { es: 'Tropical seco (24-30°C)', en: 'Dry tropical (24-30°C)' },
      bestTime: { es: 'Todo el año, especialmente nov-mayo', en: 'Year-round, especially Nov-May' },
      airport: { es: '20 min de las bahías principales', en: '20 min from main bays' },
      activities: { es: 'Snorkel, buceo, senderismo, tours en barco', en: 'Snorkeling, diving, hiking, boat tours' }
    }
  },
  'oaxaca': {
    code: 'OAX',
    name: 'Oaxaca de Juárez',
    tagline: { es: 'Cuna de la Cultura y Gastronomía Mexicana', en: 'Cradle of Mexican Culture and Gastronomy' },
    hero: 'https://images.unsplash.com/photo-1675523212590-cf205ac8ad71?w=1920&q=80',
    description: {
      es: 'Oaxaca es el corazón cultural de México, una ciudad colonial Patrimonio de la Humanidad que combina arquitectura virreinal, tradiciones milenarias, gastronomía excepcional y una escena artesanal vibrante. Rodeada de montañas, zonas arqueológicas y pueblos mágicos, Oaxaca ofrece una experiencia cultural única e inolvidable.',
      en: 'Oaxaca is the cultural heart of Mexico, a UNESCO World Heritage colonial city that combines viceregal architecture, ancient traditions, exceptional gastronomy and a vibrant craft scene. Surrounded by mountains, archaeological sites and magical towns, Oaxaca offers a unique and unforgettable cultural experience.'
    },
    highlights: [
      {
        icon: Building,
        title: { es: 'Zócalo y Centro Histórico', en: 'Zocalo and Historic Center' },
        description: { es: 'Plaza principal rodeada de portales, la Catedral y edificios coloniales coloridos. Corazón vibrante de la ciudad.', en: 'Main square surrounded by arcades, the Cathedral and colorful colonial buildings. Vibrant heart of the city.' },
        image: 'https://images.unsplash.com/photo-1675523212590-cf205ac8ad71?w=800&q=80'
      },
      {
        icon: Mountain,
        title: { es: 'Monte Albán', en: 'Monte Albán' },
        description: { es: 'Ciudad zapoteca prehispánica en lo alto de una montaña. Vistas panorámicas y pirámides impresionantes.', en: 'Pre-Hispanic Zapotec city atop a mountain. Panoramic views and impressive pyramids.' },
        image: 'https://images.unsplash.com/photo-1768092089287-316bd0cf5b9e?w=800&q=80'
      },
      {
        icon: TreePalm,
        title: { es: 'Hierve el Agua', en: 'Hierve el Agua' },
        description: { es: 'Cascadas petrificadas naturales únicas en el mundo. Pozas naturales con vistas de montañas.', en: 'Unique natural petrified waterfalls. Natural pools with mountain views.' },
        image: 'https://images.unsplash.com/photo-1740320755652-450564c1dbb8?w=800&q=80'
      },
      {
        icon: Building,
        title: { es: 'Templo de Santo Domingo', en: 'Santo Domingo Temple' },
        description: { es: 'Joya del barroco novohispano con interiores dorados espectaculares. Museo de las Culturas anexo.', en: 'Jewel of New Spanish Baroque with spectacular golden interiors. Museum of Cultures attached.' },
        image: 'https://images.unsplash.com/photo-1767733692705-1dddc4138384?w=800&q=80'
      },
      {
        icon: UtensilsCrossed,
        title: { es: 'Gastronomía y Mezcal', en: 'Gastronomy and Mezcal' },
        description: { es: 'Mole, tlayudas, chapulines y mezcal artesanal. Visita mercados y mezcalerías tradicionales.', en: 'Mole, tlayudas, grasshoppers and artisanal mezcal. Visit markets and traditional mezcal bars.' },
        image: 'https://images.unsplash.com/photo-1732797720000-98f431a93a9b?w=800&q=80'
      },
      {
        icon: Camera,
        title: { es: 'Artesanías y Mercados', en: 'Crafts and Markets' },
        description: { es: 'Alebrijes, textiles, barro negro y más. Explora mercados 20 de Noviembre y Benito Juárez.', en: 'Alebrijes, textiles, black pottery and more. Explore 20 de Noviembre and Benito Juárez markets.' },
        image: 'https://images.unsplash.com/photo-1675522305564-e4f9348aff2e?w=800&q=80'
      }
    ],
    gallery: [
      'https://images.unsplash.com/photo-1675523212590-cf205ac8ad71?w=600&q=80',
      'https://images.unsplash.com/photo-1675522305564-e4f9348aff2e?w=600&q=80',
      'https://images.unsplash.com/photo-1740320755652-450564c1dbb8?w=600&q=80',
      'https://images.unsplash.com/photo-1768092089287-316bd0cf5b9e?w=600&q=80',
      'https://images.unsplash.com/photo-1732797720000-98f431a93a9b?w=600&q=80',
      'https://images.unsplash.com/photo-1767733692705-1dddc4138384?w=600&q=80'
    ],
    prakticalInfo: {
      climate: { es: 'Templado de montaña (15-25°C)', en: 'Mild mountain climate (15-25°C)' },
      bestTime: { es: 'Octubre-abril (clima fresco y seco)', en: 'October-April (cool and dry weather)' },
      airport: { es: '15 min del centro histórico', en: '15 min from historic center' },
      activities: { es: 'Tours culturales, gastronomía, artesanías, arqueología', en: 'Cultural tours, gastronomy, crafts, archaeology' }
    }
  }
};

const DestinoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const destination = destinationsData[slug];
  
  if (!destination) {
    navigate('/');
    return null;
  }

  const getText = (textObj) => textObj[language] || textObj.es;

  const handleBookFlight = () => {
    navigate(`/rutas?destination=${destination.name}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <div 
          className="ken-burns absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${destination.hero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
        
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-2 bg-amber-500 text-white font-bold text-sm rounded-full mb-4"
            >
              {destination.code}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-7xl font-bold text-white mb-4"
            >
              {destination.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-2xl md:text-3xl text-amber-200 mb-8"
            >
              {getText(destination.tagline)}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  onClick={handleBookFlight}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8 py-6"
                >
                  <Plane className="w-5 h-5 mr-2" />
                  {language === 'es' ? `Buscar Vuelos a ${destination.name}` : `Search Flights to ${destination.name}`}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
                {language === 'es' ? '¿Por qué visitar ' : 'Why visit '}
                {destination.name}?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                {getText(destination.description)}
              </p>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInWhenVisible>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {language === 'es' ? 'Lugares Imperdibles' : 'Must-See Places'}
            </h2>
          </FadeInWhenVisible>
          
          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {destination.highlights.map((highlight, index) => {
                const IconComponent = highlight.icon;
                return (
                  <StaggerItem key={index}>
                    <CinematicCard className="overflow-hidden">
                      <div 
                        className="h-48 bg-cover bg-center image-zoom-container"
                      >
                        <div
                          className="image-zoom h-full bg-cover bg-center"
                          style={{ backgroundImage: `url(${highlight.image})` }}
                        />
                      </div>
                      <CardContent className="p-6">
                        <div className="flex items-center mb-3">
                          <motion.div
                            whileHover={{ scale: 1.1, rotate: 10 }}
                            transition={{ duration: 0.3 }}
                            className="p-2 bg-blue-100 rounded-lg mr-3"
                          >
                            <IconComponent className="w-6 h-6 text-blue-900" />
                          </motion.div>
                          <h3 className="text-xl font-bold text-gray-900">
                            {getText(highlight.title)}
                          </h3>
                        </div>
                        <p className="text-gray-600">
                          {getText(highlight.description)}
                        </p>
                      </CardContent>
                    </CinematicCard>
                  </StaggerItem>
                );
              })}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {language === 'es' ? 'Galería de Fotos' : 'Photo Gallery'}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {destination.gallery.map((image, index) => (
              <div 
                key={index}
                className="aspect-square bg-cover bg-center rounded-lg overflow-hidden hover:scale-105 transition-transform duration-300 cursor-pointer"
                style={{ backgroundImage: `url(${image})` }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Practical Info */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            {language === 'es' ? 'Información Práctica' : 'Practical Information'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card>
              <CardContent className="p-6 text-center">
                <Thermometer className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">
                  {language === 'es' ? 'Clima' : 'Climate'}
                </h3>
                <p className="text-sm text-gray-600">
                  {getText(destination.prakticalInfo.climate)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">
                  {language === 'es' ? 'Mejor Época' : 'Best Time'}
                </h3>
                <p className="text-sm text-gray-600">
                  {getText(destination.prakticalInfo.bestTime)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <MapPin className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">
                  {language === 'es' ? 'Del Aeropuerto' : 'From Airport'}
                </h3>
                <p className="text-sm text-gray-600">
                  {getText(destination.prakticalInfo.airport)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Camera className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">
                  {language === 'es' ? 'Actividades' : 'Activities'}
                </h3>
                <p className="text-sm text-gray-600">
                  {getText(destination.prakticalInfo.activities)}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'es' ? '¿Listo para tu aventura?' : 'Ready for your adventure?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {language === 'es' 
              ? `Reserva tu vuelo a ${destination.name} y vive una experiencia inolvidable` 
              : `Book your flight to ${destination.name} and live an unforgettable experience`}
          </p>
          <Button 
            size="lg" 
            onClick={handleBookFlight}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8 py-6"
          >
            <Plane className="w-5 h-5 mr-2" />
            {language === 'es' ? 'Buscar Vuelos Ahora' : 'Search Flights Now'}
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DestinoPage;
